// Small inline SVG icons (kill feed tags, equipment, objective arrow). 24x24 viewBox, `currentColor`.

export type IconName =
  | "headshot"
  | "noscope"
  | "midair"
  | "wallrun"
  | "longshot"
  | "sliding"
  | "world"
  | "suicide"
  | "bomb"
  | "frag"
  | "throwing_knife"
  | "flash"
  | "smoke"
  | "impulse"
  | "generic"
  | "arrow";

const FILL = 'fill="currentColor" stroke="none"';

const ICONS: Readonly<Record<IconName, string>> = {
  headshot: `<circle cx="12" cy="12" r="7.5"/><circle cx="12" cy="12" r="2" ${FILL}/><path d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4"/>`,
  noscope: `<circle cx="12" cy="12" r="8"/><path d="M6.3 17.7L17.7 6.3"/><circle cx="12" cy="12" r="1.6" ${FILL}/>`,
  midair: `<path d="M5 13l7-7 7 7M5 20l7-7 7 7"/>`,
  wallrun: `<path d="M5 3v18" stroke-width="3"/><path d="M9 19l9-9M12 10h6v6"/>`,
  longshot: `<path d="M2.5 12h19M17 7.5l4.5 4.5-4.5 4.5"/><path d="M7 9.5v5M12 10.5v3"/>`,
  sliding: `<path d="M3 20h18"/><path d="M6 16l7-3 6 3"/><circle cx="6.5" cy="9" r="2.2" ${FILL}/>`,
  world: `<path d="M12 3v13M6.5 11L12 16.5 17.5 11"/><path d="M4 21h16"/>`,
  suicide: `<circle cx="12" cy="12" r="8.5"/><path d="M8.5 8.5l7 7M15.5 8.5l-7 7"/>`,
  bomb: `<circle cx="11" cy="14" r="6.5" ${FILL}/><path d="M15.5 9l3-3.5M18.5 2.5v3M17 4h3"/>`,
  frag: `<ellipse cx="12" cy="14.5" rx="6.5" ry="7.5" ${FILL}/><path d="M9 6.5h6M12 3v3.5M15 4.5l3 .8"/>`,
  throwing_knife: `<path d="M21 3L9 8.5l6.5 6.5z" ${FILL}/><path d="M9.5 14.5L3.5 20.5" stroke-width="3"/>`,
  flash: `<circle cx="12" cy="12" r="3.5" ${FILL}/><path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l2.8 2.8M16.2 16.2L19 19M19 5l-2.8 2.8M7.8 16.2L5 19"/>`,
  smoke: `<path d="M7 19a4.5 4.5 0 010-9 5.5 5.5 0 0110.5-1.2A4.6 4.6 0 0117 19z" ${FILL}/>`,
  impulse: `<circle cx="12" cy="12" r="2.5" ${FILL}/><circle cx="12" cy="12" r="6.5"/><circle cx="12" cy="12" r="10" stroke-opacity=".55"/>`,
  generic: `<circle cx="12" cy="12" r="6" ${FILL}/>`,
  arrow: `<path d="M12 2.5l8 18-8-4.5-8 4.5z" ${FILL}/>`,
};

const templates = new Map<IconName, SVGSVGElement>();

/** A fresh SVG element for `name`. The markup is static (not user data). */
export function icon(name: IconName, cls = "hud-icon"): SVGSVGElement {
  let tpl = templates.get(name);
  if (!tpl) {
    const holder = document.createElement("template");
    holder.innerHTML =
      `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${ICONS[name]}</svg>`;
    tpl = holder.content.firstElementChild as SVGSVGElement;
    templates.set(name, tpl);
  }
  const node = tpl.cloneNode(true) as SVGSVGElement;
  node.setAttribute("class", cls);
  return node;
}

const TAG_ICONS: Readonly<Record<string, IconName>> = {
  noscope: "noscope",
  midair: "midair",
  wallrun: "wallrun",
  longshot: "longshot",
  sliding: "sliding",
};

export function tagIcon(tag: string): IconName | null {
  return TAG_ICONS[tag] ?? null;
}

const EQUIPMENT_ICONS: Readonly<Record<string, IconName>> = {
  frag: "frag",
  throwing_knife: "throwing_knife",
  flash: "flash",
  smoke: "smoke",
  impulse: "impulse",
};

export function equipmentIcon(id: string): IconName {
  return EQUIPMENT_ICONS[id] ?? "generic";
}

/** Icon that stands in for the weapon label in the kill feed for non-weapon causes of death. */
export function causeIcon(weapon: string): IconName | null {
  return weapon === "bomb" ? "bomb" : weapon === "world" ? "world" : weapon === "suicide" ? "suicide" : null;
}
